import * as root from '@app/app.state';

import { TeamMember } from '@entities/team-member';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Role } from '@entities/role';
import { Competency } from '@entities/competency';
import { PerformanceEvaluation } from '@entities/performance-evaluation';
import { CompetencyLevel } from '@entities/competency-level';
import { TaskRating } from '@entities/task-rating';
import { FilterValues } from '@app/shared/interfaces/filter.interfaces';
import {
   getBusinessUnits,
   getDepartmentFunctions,
   getDepartments,
} from '@app/org-builder/state/organization.state';
import { BusinessUnit } from '@entities/business-unit';
import { Department } from '@entities/department';
import { DepartmentFunction } from '@entities/department-function';

export interface State extends root.State {
   team: TeamState;
}

export interface TeamState {
   teamMembers: TeamMember[];
   selectedTeamMemberId: string | null;
   roles: Role[];
   selectedRoleId: string;
   competencies: Competency[];
   selectedCompetencyId: string;
   performanceEvaluations: PerformanceEvaluation[];
   selectedPerformanceEvaluationId: string;
   competencyLevels: CompetencyLevel[];
   selectedTaskRatingTreeIds: string[];
   tasksFilter: FilterValues;
   teamMembersDisplayedColumns: string[];
}

export interface TaskRatingTreeItem {
   id: string;
   name: string;
   rating: number;
   children?: TaskRatingTreeItem[];
   individualRatings?: TaskRating[];
}

export const getTeamState = createFeatureSelector<TeamState>('team');
export const getTeamMembers = createSelector(getTeamState, (state) => state.teamMembers);
export const getTeamMember = createSelector(getTeamState, (state, props) =>
   state.teamMembers.find((t) => t.id == props.id),
);
export const getSelectedTeamMember = createSelector(getTeamState, (state) =>
   state.teamMembers.find((t) => t.id == state.selectedTeamMemberId),
);

export const getRoles = createSelector(getTeamState, (state) => state.roles);

export const getSelectedRole = createSelector(getTeamState, (state) =>
   state.roles.find((r) => r.id == state.selectedRoleId),
);

export const getTeamMembersForSelectedRole = createSelector(getTeamState, (state) => {
   const selectedRole = state.roles.find((r) => r.id == state.selectedRoleId);
   if (selectedRole && selectedRole.teamMembers) {
      return state.teamMembers.filter((t) => selectedRole.teamMembers.includes(t.id));
   } else {
      return [];
   }
});

export const getGrowthPlans = createSelector(getSelectedTeamMember, (teamMember) => {
   if (teamMember) {
      return teamMember.growthPlans;
   } else {
      return [];
   }
});

export const getCompetencies = createSelector(getTeamState, (state) => state.competencies);
export const getSelectedCompetency = createSelector(getTeamState, (state) =>
   state.competencies.find((c) => c.id === state.selectedCompetencyId),
);
export const getAssignedCompetencies = createSelector(
   getSelectedTeamMember,
   getCompetencies,
   (teamMember, competencies) => {
      if (teamMember && teamMember.assignedTasks && competencies) {
         return competencies
            .filter((c) => teamMember.assignedTasks.some((id) => c.linkedTasks.includes(id)))
            .map((comp) => ({
               ...comp,
               linkedTasks: comp.linkedTasks.filter((id) => teamMember.assignedTasks?.includes(id)),
            }));
      } else {
         return [];
      }
   },
);

const getPerformanceEvaluations = createSelector(getTeamState, (state, props) =>
   state.performanceEvaluations.filter((perfEval) => perfEval.teamMemberId === props.teamMemberId),
);

const getSelectedPerformanceEvaluation = createSelector(getTeamState, (state) =>
   state.performanceEvaluations.find(
      (perfEval) => perfEval.id === state.selectedPerformanceEvaluationId,
   ),
);

const getCompetencyLevels = createSelector(getTeamState, (state) => state.competencyLevels);
const getSelectedTaskRatingTreeIds = createSelector(
   getTeamState,
   (state) => state.selectedTaskRatingTreeIds,
);
const getTeamTasksFilter = createSelector(getTeamState, (state) => state.tasksFilter);
const getTeamMembersDisplayedColumns = createSelector(
   getTeamState,
   (state) => state.teamMembersDisplayedColumns,
);

const getLeadershipAssignments = createSelector(
   getSelectedTeamMember,
   getBusinessUnits,
   getDepartments,
   getDepartmentFunctions,
   (teamMember, businessUnits, departments, deptFns) => {
      let director: BusinessUnit[] = [];
      let manager: Department[] = [];
      let lead: DepartmentFunction[] = [];
      let assignments: Array<{
         role: string;
         businessUnit?: BusinessUnit;
         department?: Department;
         departmentFunction?: DepartmentFunction;
         index?: number;
      }> = [];
      if (teamMember?.id) {
         director = businessUnits.filter((bu) => bu.directorId === teamMember.id);
         manager = departments.filter((dept) => dept.managerId === teamMember.id);
         lead = deptFns.filter((deptFn) => deptFn.leadId === teamMember.id);
      }
      director.forEach((businessUnit) => {
         assignments.push({ role: 'Director', businessUnit });
      });
      manager.forEach((department) => {
         const businessUnit = businessUnits.find((bu) => bu.id === department.businessUnitId);
         assignments.push({ role: 'Manager', businessUnit, department });
      });
      lead.forEach((departmentFunction) => {
         const department = departments.find((dept) => dept.id == departmentFunction.departmentId);
         let businessUnit;
         if (department) {
            businessUnit = businessUnits.find((bu) => bu.id === department.businessUnitId);
         }
         assignments.push({ role: 'Lead', businessUnit, department, departmentFunction });
      });
      assignments = assignments
         .sort((a, b) => {
            const aRoleVal = a.role === 'Director' ? 3 : a.role === 'Manager' ? 2 : 1;
            const bRoleVal = b.role === 'Director' ? 3 : b.role === 'Manager' ? 2 : 1;
            if (aRoleVal === bRoleVal) {
               const buCompare = a.businessUnit?.name?.localeCompare(b.businessUnit?.name);
               if (buCompare === 0) {
                  const deptCompare = a.department?.name?.localeCompare(b.department?.name);
                  if (deptCompare === 0) {
                     return a.departmentFunction?.name?.localeCompare(b.departmentFunction?.name);
                  } else {
                     return deptCompare;
                  }
               } else {
                  return buCompare;
               }
            } else {
               return bRoleVal - aRoleVal;
            }
         })
         .map((item, index) => ({ ...item, index }));
      return assignments;
   },
);

const getHasLeadershipAssignments = createSelector(
   getLeadershipAssignments,
   (leadershipAssignments) => {
      return leadershipAssignments.length > 0;
   },
);

export const teamSelectors = {
   getTeamMembers,
   getTeamMember,
   getSelectedTeamMember,
   getRoles,
   getSelectedRole,
   getCompetencies,
   getSelectedCompetency,
   getAssignedCompetencies,
   getPerformanceEvaluations,
   getSelectedPerformanceEvaluation,
   getCompetencyLevels,
   getSelectedTaskRatingTreeIds,
   getTeamTasksFilter,
   getTeamMembersDisplayedColumns,
   getLeadershipAssignments,
   getHasLeadershipAssignments,
};
